import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Star } from "lucide-react";
import { BuyButton } from "./BuyButton";
import { BookTilt } from "./BookTilt";
import { scrollToSection } from "./constants";

const EASE = [0.16, 1, 0.3, 1];

const MaskedLine = ({ children, delay }) => (
  <span className="mask-line">
    <motion.span
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.15, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bookY = useTransform(scrollYProgress, [0, 1], [0, 140]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen items-center overflow-hidden pt-28 pb-16 sm:pt-32"
    >
      <div className="pointer-events-none absolute -right-40 -top-40 h-[34rem] w-[34rem] rounded-full bg-blush/70 blur-3xl" />
      <div className="pointer-events-none absolute -left-48 bottom-0 h-[28rem] w-[28rem] rounded-full bg-sagelight/60 blur-3xl" />

      <div className="relative z-10 mx-auto grid w-full max-w-7xl items-center gap-16 px-6 sm:px-12 lg:grid-cols-[1.15fr_1fr] lg:gap-8 lg:px-20">
        <motion.div style={{ y: textY, opacity: fade }}>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.35, ease: EASE }}
            className="mb-6 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.28em] text-terracotta"
          >
            <span className="h-px w-10 bg-terracotta/60" />
            A Daily Gratitude Journal
          </motion.p>

          <h1
            data-testid="hero-title"
            className="font-serif text-4xl font-medium leading-[1.06] tracking-tight text-espresso sm:text-5xl lg:text-6xl"
          >
            <MaskedLine delay={0.45}>Rewire your days</MaskedLine>
            <MaskedLine delay={0.58}>for quiet joy.</MaskedLine>
            <MaskedLine delay={0.71}>
              <em className="font-medium italic text-terracotta">
                Love the life you live.
              </em>
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.0, ease: EASE }}
            className="mt-7 max-w-lg text-base leading-relaxed text-walnut sm:text-lg"
          >
            A beautifully simple 120-page journal that helps you capture the
            moments worth thanking for — and uncover a happier you, one page
            at a time.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 1.15, ease: EASE }}
            className="mt-9 flex flex-wrap items-center gap-5"
          >
            <BuyButton testId="hero-buy-now-button" label="Buy Now — $12.99" />
            <button
              data-testid="hero-explore-button"
              onClick={() => scrollToSection("about")}
              className="group inline-flex items-center gap-2 text-sm font-semibold text-espresso transition-colors hover:text-terracotta"
            >
              Explore the journal
              <ArrowDown
                size={16}
                className="transition-transform duration-300 group-hover:translate-y-0.5"
              />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.4 }}
            className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs font-medium uppercase tracking-[0.18em] text-sand"
          >
            <span className="flex items-center gap-1.5">
              <Star size={12} className="fill-gold text-gold" /> Paperback
            </span>
            <span>120 Pages</span>
            <span>6 × 9 in</span>
            <span>Ships with Prime</span>
          </motion.div>
        </motion.div>

        <motion.div style={{ y: bookY }} className="relative">
          <BookTilt />
        </motion.div>
      </div>
    </section>
  );
};
